import { ReactElement, useState, cloneElement } from "react";
import "./style.scss";

type Status = "error" | "warning" | "success";
type Mode = "focus" | "value";

type ChildProps = {
  placeholder?: string;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  prefix?: React.ReactNode;
  value?: unknown;
};

type Props<P extends ChildProps> = {
  label: string;
  placeholder?: string;
  hasPrefix?: boolean;
  status?: Status;
  className?: string;
  mode?: Mode;
  hasValue?: boolean;
  children: ReactElement<P>;
};

function hasAValue(v: unknown): boolean {
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (v instanceof Date) return true;
  if (typeof v === "object") return true;
  return String(v).trim().length > 0;
}

function FloatFormGroup<P extends ChildProps>({
  label,
  placeholder,
  hasPrefix,
  status,
  className = "",
  mode = "focus",
  hasValue: hasValueOverride,
  children,
}: Props<P>) {
  const [focused, setFocused] = useState(false);

  const childProps = children.props;
  const childHasPrefix = "prefix" in childProps && childProps.prefix != null;
  const computedHasPrefix = hasPrefix ?? !!childHasPrefix;
  const childHasValue =
    "value" in childProps ? hasAValue(childProps.value) : false;

  const hasValue = hasValueOverride ?? childHasValue;

  const isActive = mode === "focus" ? focused || hasValue : hasValue;

  const handleFocus: NonNullable<ChildProps["onFocus"]> = (e) => {
    setFocused(true);
    childProps.onFocus?.(e);
  };
  const handleBlur: NonNullable<ChildProps["onBlur"]> = (e) => {
    setFocused(false);
    childProps.onBlur?.(e);
  };

  const nextPlaceholder = !isActive ? " " : "";

  const patchProps: Partial<ChildProps> = {
    placeholder: nextPlaceholder,
    onFocus: handleFocus,
    onBlur: handleBlur,
  };

  const cloned = cloneElement(children, patchProps as Partial<P>);

  return (
    <div
      className={[
        "fl-item",
        isActive && "fl-active",
        computedHasPrefix && "fl-has-prefix",
        status && `fl-${status}`,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <label className="fl-label">{label}</label>
      {cloned}
    </div>
  );
}

export default FloatFormGroup;
